import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  news: [],
  selectedNews: null,
  pagination: null,
  subscription: null,
  isFetching: false,
  detailFetching: false,
  subscriptionFetching: false,
  subscriptionError: false,
  subscriptionErrorMessage: null,
  actionFetching: false,
  error: false,
  errorMessage: null,
  saveError: false,
  saveErrorMessage: null,
};

const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.communication_posts)) return value.communication_posts;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.news)) return value.news;
  return [];
};

const extractNewsId = (item) =>
  item?.id_communication_post ??
  item?.communication_post_id ??
  item?.id ??
  item?.idRecommendation ??
  null;

const normalizeListPayload = (payload) => safeArray(payload);

const normalizeDetailPayload = (payload) => {
  if (!payload) return null;

  if (payload?.communication_post) {
    return payload.communication_post;
  }

  return payload;
};

const upsertNewsInList = (items = [], updatedItem = null) => {
  const id = extractNewsId(updatedItem);

  if (!id) {
    return items;
  }

  const nextItem = {
    ...updatedItem,
    id_communication_post:
      updatedItem?.id_communication_post ?? updatedItem?.id ?? id,
  };

  const existingIndex = items.findIndex(
    (item) => Number(extractNewsId(item)) === Number(id),
  );

  if (existingIndex === -1) {
    return [nextItem, ...items];
  }

  const nextItems = [...items];
  nextItems[existingIndex] = {
    ...nextItems[existingIndex],
    ...nextItem,
  };

  return nextItems;
};

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    newsStart: (state) => {
      state.isFetching = true;
      state.error = false;
      state.errorMessage = null;
    },
    newsSuccess: (state, action) => {
      state.isFetching = false;
      state.error = false;
      state.errorMessage = null;
      state.news = normalizeListPayload(action.payload);
      state.pagination = action.payload?.pagination || {
        total: Array.isArray(state.news) ? state.news.length : 0,
      };
    },
    newsFailure: (state, action) => {
      state.isFetching = false;
      state.error = true;
      state.errorMessage = action.payload || null;
    },
    newsDetailStart: (state) => {
      state.detailFetching = true;
      state.error = false;
      state.errorMessage = null;
    },
    newsDetailSuccess: (state, action) => {
      state.detailFetching = false;
      state.error = false;
      state.errorMessage = null;
      state.selectedNews = normalizeDetailPayload(action.payload);
    },
    newsDetailFailure: (state, action) => {
      state.detailFetching = false;
      state.error = true;
      state.errorMessage = action.payload || null;
    },
    newsSubscriptionStart: (state) => {
      state.subscriptionFetching = true;
      state.subscriptionError = false;
      state.subscriptionErrorMessage = null;
    },
    newsSubscriptionSuccess: (state, action) => {
      state.subscriptionFetching = false;
      state.subscriptionError = false;
      state.subscriptionErrorMessage = null;
      state.subscription = action.payload || null;
    },
    newsSubscriptionFailure: (state, action) => {
      state.subscriptionFetching = false;
      state.subscriptionError = true;
      state.subscriptionErrorMessage = action.payload || null;
    },
    newsActionStart: (state) => {
      state.actionFetching = true;
      state.saveError = false;
      state.saveErrorMessage = null;
    },
    newsActionSuccess: (state, action) => {
      state.actionFetching = false;
      state.saveError = false;
      state.saveErrorMessage = null;

      const payload = action.payload || {};
      const updatedItem =
        payload?.communication ||
        payload?.communication_post ||
        payload?.news ||
        payload?.updatedNews ||
        payload;

      state.news = upsertNewsInList(state.news, updatedItem);

      if (
        state.selectedNews &&
        Number(extractNewsId(state.selectedNews)) ===
          Number(extractNewsId(updatedItem))
      ) {
        state.selectedNews = {
          ...state.selectedNews,
          ...updatedItem,
        };
      }
    },
    newsActionFailure: (state, action) => {
      state.actionFetching = false;
      state.saveError = true;
      state.saveErrorMessage = action.payload || null;
    },
    clearSelectedNews: (state) => {
      state.selectedNews = null;
      state.detailFetching = false;
    },
    clearNewsSubscription: (state) => {
      state.subscription = null;
      state.subscriptionFetching = false;
      state.subscriptionError = false;
      state.subscriptionErrorMessage = null;
    },
    logoutInfoNews: () => initialState,
  },
});

export const {
  newsStart,
  newsSuccess,
  newsFailure,
  newsDetailStart,
  newsDetailSuccess,
  newsDetailFailure,
  newsSubscriptionStart,
  newsSubscriptionSuccess,
  newsSubscriptionFailure,
  newsActionStart,
  newsActionSuccess,
  newsActionFailure,
  clearSelectedNews,
  clearNewsSubscription,
  logoutInfoNews,
} = newsSlice.actions;

export default newsSlice.reducer;
